import { useState, useEffect } from 'react';
import { Search, Mail, Calendar, Award, User } from 'lucide-react';
import adminService from '../../services/adminService';
import ceuService from '../../services/unifiedCEUService';
import { s3PhotoService } from '../../services/s3PhotoService';
import LoadingSpinner from '../ui/LoadingSpinner';
import Alert from '../ui/Alert';
import Card from '../ui/Card';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const result = await adminService.getAllUsers();
      
      if (!result.success) {
        throw new Error(result.error);
      }

      // Load CEU counts and photos for each user
      const usersWithCEUs = await Promise.all(
        result.users.map(async (user) => {
          try {
            // Use admin service to get CEU data (bypasses API restrictions)
            const ceusResult = await adminService.getUserCEUs(user.userId);
            const ceus = ceusResult.success ? ceusResult.data : [];
            const currentCycleCEUs = ceus.filter(ceu => ceu.isCurrentCycle);
            const totalCEUs = currentCycleCEUs.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);
            
            // Generate pre-signed URL for profile photo
            let photoUrl = null;
            if (user.profilePhoto) {
              photoUrl = await s3PhotoService.getPhotoUrl(user.profilePhoto);
            } else if (user.profilePhotoUrl) {
              // Legacy field - generate pre-signed URL using userId
              photoUrl = await s3PhotoService.getPhotoUrl(user.userId);
            }
            
            return {
              ...user,
              photoUrl,
              totalCEUs: totalCEUs.toFixed(1),
              workshopCount: currentCycleCEUs.length
            };
          } catch {
            return { ...user, photoUrl: null, totalCEUs: 0, workshopCount: 0 };
          }
        })
      );

      setUsers(usersWithCEUs);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  const activeUsers = users.filter(u => u.workshopCount > 0).length;

  return (
    <div className="space-y-6">
      {/* Header & Search */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">Manage users and track their CEU progress</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <h3 className="text-3xl font-bold text-gray-900">{users.length}</h3>
          <p className="text-gray-600 mt-1">Total Users</p>
        </Card>
        <Card className="p-6">
          <h3 className="text-3xl font-bold text-gray-900">{activeUsers}</h3>
          <p className="text-gray-600 mt-1">Active Users</p>
        </Card>
        <Card className="p-6">
          <h3 className="text-3xl font-bold text-gray-900">
            {users.reduce((sum, u) => sum + parseFloat(u.totalCEUs || 0), 0).toFixed(1)}
          </h3>
          <p className="text-gray-600 mt-1">Total CEUs Tracked</p>
        </Card>
      </div>

      {/* Users Table - Desktop */}
      <Card className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CEU Progress
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.userId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      {user.photoUrl ? (
                        <img src={user.photoUrl} alt={`${user.name || 'User'}'s profile photo`} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        {user.role === 'admin' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      {user.email}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                      <Calendar className="w-3 h-3" />
                      Joined {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="font-medium text-gray-900">{user.certifyingBody || 'RID'}</div>
                        <div className="text-xs text-gray-500">{user.membershipType || 'NIC'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{user.totalCEUs} CEUs</div>
                      <div className="text-xs text-gray-500">{user.workshopCount} workshops</div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Users Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {filteredUsers.map((user) => (
          <Card key={user.userId} className="p-4">
            <div className="flex items-start gap-3 mb-3">
              {user.photoUrl ? (
                <img src={user.photoUrl} alt={`${user.name || 'User'}'s profile photo`} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                  {user.role === 'admin' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                      Admin
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Award className="w-4 h-4" />
                <span>{user.certifyingBody || 'RID'}</span>
              </div>
              <div className="font-medium text-gray-900">
                {user.totalCEUs} CEUs
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No users found matching "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
}
